import { supabase } from '../../integrations/supabase/client';
import { toast } from 'sonner';
import { createOrGetChat, getExistingPairChat, canonicalPair, ChatCache } from '../chat/chatService';

// VCHAT-FINAL-01: Strict UUID validation regex
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * V323.BUG2: Pick the newest ACCEPTED friend connection for the pair.
 */
function pickNewestAcceptedConnection(
  rows: Array<{
    id: string;
    user_id: string;
    connection_user_id: string;
    status: string;
    type: string;
    created_at?: string;
  }>,
  meId: string
) {
  const mine = rows.filter((r) => r.user_id === meId);
  const candidates = mine.length ? mine : rows;
  return candidates
    .slice()
    .sort((a, b) => {
      const at = a.created_at ? Date.parse(a.created_at) : 0;
      const bt = b.created_at ? Date.parse(b.created_at) : 0;
      if (at !== bt) return bt - at;
      return b.id.localeCompare(a.id);
    })[0];
}

/** V323.BUG2: Fetch ACCEPTED FRIEND connections between two users */
async function fetchAcceptedFriendConnections(meId: string, otherId: string) {
  const { data, error } = await supabase
    .from('connections')
    .select('id, user_id, connection_user_id, status, type, created_at')
    .or(
      `and(user_id.eq.${meId},connection_user_id.eq.${otherId}),and(user_id.eq.${otherId},connection_user_id.eq.${meId})`
    )
    .eq('status', 'ACCEPTED')
    .eq('type', 'FRIEND')
    .order('created_at', { ascending: false });

  if (error) {
    const msg = error.message || String(error);
    if (import.meta.env.DEV) console.error('[chat] fetchAcceptedFriendConnections failed:', error);
    throw new Error(`Failed to resolve friend connection: ${msg}`);
  }
  return data || [];
}

export type PairChatIntent = 'match' | 'friend';

/**
 * V323: Single entry point — match wins, at most one chat per pair.
 * - intent='match': verify active match, createOrGetChat(match). NEVER call friend RPC.
 * - intent='friend': check match first; if match chat exists return it. Else createOrGetChat(friend).
 */
export async function getOrCreatePairChatRoute(
  meId: string,
  otherId: string,
  opts?: { intent?: PairChatIntent }
): Promise<{ chatId: string; connectionId: string | null }> {
  if (!meId || !otherId) throw new Error('Missing user id');
  if (meId === otherId) throw new Error('Cannot create a chat with yourself');

  const intent = opts?.intent ?? 'friend';
  const { low, high } = canonicalPair(meId, otherId);

  if (import.meta.env.DEV) {
    console.log('[chat] getOrCreatePairChatRoute', { pair: { low, high }, intent });
  }

  // 1. Check existing chat — match dominates always
  const existingChat = await getExistingPairChat(low, high);
  if (existingChat?.chat_type === 'match') {
    if (intent === 'match') {
      if (import.meta.env.DEV) console.log('[chat] getOrCreatePairChatRoute: existing match chat', existingChat.id);
      return { chatId: existingChat.id, connectionId: existingChat.connection_id ?? null };
    }
    // V323.BUG3: intent='friend' — migrate match chat to friend instead of creating new row
    const connections = await fetchAcceptedFriendConnections(meId, otherId);
    if (connections.length === 0) {
      throw new Error('No ACCEPTED FRIEND connection found for this user');
    }
    const best = pickNewestAcceptedConnection(connections, meId);
    const connectionId = best.id;
    const { error: updateErr } = await supabase
      .from('chats')
      .update({
        chat_type: 'friend',
        connection_id: connectionId,
        match_id: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existingChat.id);

    if (!updateErr) {
      if (import.meta.env.DEV) {
        console.log('[chat] V323.BUG3: Migrated match chat to friend, same row:', existingChat.id);
      }
      ChatCache.clear(meId);
      ChatCache.clear(otherId);
      return { chatId: existingChat.id, connectionId };
    }
    // Fallback: return match chat even if update failed (preserve history, avoid fork)
    if (import.meta.env.DEV) {
      console.warn('[chat] V323.BUG3: Update failed, returning match chat as-is:', updateErr);
    }
    return { chatId: existingChat.id, connectionId };
  }

  // 2. Match intent: verify active match, createOrGetChat(match). NEVER friend RPC.
  if (intent === 'match') {
    const { data: matchRows, error: matchErr } = await supabase
      .from('matches')
      .select('id')
      .or(
        `and(user1_id.eq.${meId},user2_id.eq.${otherId}),and(user1_id.eq.${otherId},user2_id.eq.${meId})`
      )
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1);

    if (matchErr || !matchRows?.length) {
      throw new Error('No active match found for this user');
    }

    const chat = await createOrGetChat({
      userA: meId,
      userB: otherId,
      chatType: 'match',
      matchId: matchRows[0].id,
      connectionId: null,
    });
    if (import.meta.env.DEV) console.log('[chat] getOrCreatePairChatRoute: match path', chat.id);
    return { chatId: chat.id, connectionId: chat.connection_id ?? null };
  }

  // 3. Friend intent: createOrGetChat(friend) — no RPC, deterministic
  const connections = await fetchAcceptedFriendConnections(meId, otherId);
  if (connections.length === 0) {
    throw new Error('No ACCEPTED FRIEND connection found for this user');
  }

  const best = pickNewestAcceptedConnection(connections, meId);
  const connectionId = best.id;

  const chat = await createOrGetChat({
    userA: meId,
    userB: otherId,
    chatType: 'friend',
    connectionId,
    matchId: null,
  });

  if (typeof chat.id !== 'string' || !UUID_RE.test(chat.id)) {
    throw new Error('Invalid chat id returned');
  }

  if (import.meta.env.DEV) {
    console.log('[chat] getOrCreatePairChatRoute: friend path', chat.id);
  }

  return { chatId: chat.id, connectionId };
}

/** @deprecated Use getOrCreatePairChatRoute */
export async function getOrCreateFriendChatRoute(
  meId: string,
  otherId: string,
  _opts?: { allowMatchFallback?: boolean }
): Promise<{ chatId: string; connectionId: string | null }> {
  return getOrCreatePairChatRoute(meId, otherId, { intent: 'friend' });
}

/**
 * @deprecated Use navigateToFriendChat(currentUserId, otherUserId, otherUserName, navigate, { isMatch: true })
 */
export async function navigateToMatchChat(
  currentUserId: string,
  matchedUserId: string,
  matchedUserName: string,
  navigate: (path: string, options?: { state?: any }) => void
): Promise<string | null> {
  return navigateToFriendChat(currentUserId, matchedUserId, matchedUserName, navigate, { isMatch: true });
}

/**
 * Creates or finds chat and navigates to it.
 * Uses getOrCreatePairChatRoute with intent derived from opts.isMatch.
 */
export async function navigateToFriendChat(
  currentUserId: string,
  friendUserId: string,
  friendUserName: string,
  navigate: (path: string, options?: { state?: any }) => void,
  opts?: { isMatch?: boolean }
): Promise<string | null> {
  try {
    if (!currentUserId || !friendUserId) {
      toast.error('Unable to open chat: missing user data');
      return null;
    }

    if (currentUserId === friendUserId) {
      toast.error('You cannot create a chat with yourself');
      return null;
    }

    const intent = opts?.isMatch ? 'match' : 'friend';
    const { chatId, connectionId } = await getOrCreatePairChatRoute(currentUserId, friendUserId, { intent });
    if (!chatId || !UUID_RE.test(chatId)) {
      throw new Error('Chat route is invalid');
    }

    if (import.meta.env.DEV) {
      console.debug('[chat] navigateToFriendChat resolved', { chatId, connectionId });
    }

    navigate(`/chat/${chatId}`, {
      state: {
        userName: friendUserName,
        userId: friendUserId,
      }
    });

    return chatId;

  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[chat] navigateToFriendChat error:', error);
    }
    const errorMessage = error instanceof Error ? error.message : 'Failed to navigate to chat';
    toast.error(errorMessage);
    return null;
  }
}

/**
 * @deprecated Use navigateToFriendChat with isMatch: true
 */
export async function navigateToMatchChatWithLocation(
  currentUserId: string,
  matchedUserId: string,
  matchedUserName: string
): Promise<string | null> {
  return navigateToFriendChat(
    currentUserId,
    matchedUserId,
    matchedUserName,
    (path: string) => {
      window.location.href = path;
    },
    { isMatch: true }
  );
}
